import { db } from "./firebase";
import {
    doc,
    getDoc,
} from "firebase/firestore";

import {
    Room,
    RoomEvent,
    Track,
    createLocalVideoTrack,
    createLocalAudioTrack,
    VideoPresets
} from 'livekit-client'


class LiveKitService {
    constructor() {
        this.room = null
        this.localTracks = new Map()
        this.isConnected = false

        // Callbacks (same interface as your WebRTC service)
        this.onParticipantConnectedCallbacks = []
        this.onParticipantDisconnectedCallbacks = []
        this.onTrackSubscribedCallbacks = []
        this.onConnectionStateCallbacks = []
        this.onTrackUnsubscribedCallbacks = []
        this.processedTracks = new Set()
        // State tracking
        this.isCameraEnabled = false
        this.isMicrophoneEnabled = false
        this.isScreenSharing = false
        this.participantCameraStreams = new Map()
        this.participantScreenStreams = new Map()
        console.log('LiveKit service initialized (unauthenticated mode)')
    }

    onTrackUnsubscribed(callback) {
        this.onTrackUnsubscribedCallbacks.push(callback)
    }

    createEmptyStream() {
        return new MediaStream()
    }

    async joinRoom(roomName, participantName) {

        try {
            console.log(`Joining room: ${roomName} as ${participantName}`)

            const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/identity/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    "roomName": roomName,
                    "participantName": participantName,
                    "userEmail": participantName
                }),
            });

            const result = await response.json();
            const token = result.token;

            // Connect to LiveKit Cloud
            const serverUrl = process.env.REACT_APP_LIVEKIT_URL

            this.room = new Room({
                autoSubscribe: true,
            })

            await this.room.connect(serverUrl, token)

            this.isConnected = true
            this.setupRoomEventHandlers()

            console.log('Successfully connected to LiveKit:', this.room.name)

            // NEW: Handle existing participants in the room
            console.log('Checking for existing participants...')
            console.log(`Room has ${this.room.remoteParticipants.size} existing participants`)

            this.room.remoteParticipants.forEach((participant) => {
                console.log('Found existing participant:', participant.identity)

                // Create empty streams for both camera and screen share
                const emptyCameraStream = this.createEmptyStream()
                const emptyScreenStream = this.createEmptyStream()
                this.participantCameraStreams.set(participant.identity, emptyCameraStream)
                this.participantScreenStreams.set(participant.identity, emptyScreenStream)

                // Notify about existing participant with camera stream
                this.onParticipantConnectedCallbacks.forEach(callback =>
                    callback(participant.identity, emptyCameraStream, {
                        isCameraOn: false,
                        isScreenSharing: false,
                        hasJoined: true,
                        isEmpty: true,
                        isExisting: true // Flag to indicate this was already in room
                    })
                )

                // Check if they have any existing published tracks
                participant.trackPublications.forEach((publication) => {
                    console.log(`Checking publication: ${publication.kind} from ${participant.identity}, subscribed: ${publication.isSubscribed}`)

                    if (publication.isSubscribed && publication.track) {
                        console.log(`Found existing track: ${publication.track.kind} from ${participant.identity}`)
                        this.handleExistingTrack(participant.identity, publication.track, publication)
                    }
                })
            })

            console.log(`Processed ${this.room.remoteParticipants.size} existing participants`)

            // Start with microphone only (muted)
            await this.enableMicrophone(false)

            return {
                roomId: this.room.name,
                participantId: this.room.localParticipant.identity,
                numParticipants: this.room.numParticipants
            }

        } catch (error) {
            console.error('Failed to join room:', error)
            throw error
        }
    }

    handleExistingTrack(participantId, track, publication) {
        console.log(`Processing existing track: ${track.kind} from ${participantId}, source: ${track.source}`)

        if (!track.mediaStreamTrack) {
            console.warn('Existing track has no mediaStreamTrack')
            return
        }

        // FIXED: Use separate stream maps based on track source
        const isScreenShare = track.source === Track.Source.ScreenShare
        const streamMap = isScreenShare ? this.participantScreenStreams : this.participantCameraStreams

        // Get existing stream or create new one
        let mediaStream = streamMap.get(participantId)
        if (!mediaStream) {
            mediaStream = this.createEmptyStream()
            streamMap.set(participantId, mediaStream)
            console.log(`Created new ${isScreenShare ? 'screen share' : 'camera'} stream for existing track from ${participantId}`)
        }

        // Remove existing tracks of the same kind and add new track
        const existingTracks = mediaStream.getTracks().filter(t => t.kind === track.kind)
        existingTracks.forEach(t => {
            mediaStream.removeTrack(t)
            t.stop()
        })

        // Add the existing track
        mediaStream.addTrack(track.mediaStreamTrack)

        // Determine track properties
        const isScreenSharing = track.source === Track.Source.ScreenShare
        const isCameraOn = track.kind === 'video' && !isScreenSharing

        console.log(`Notifying about existing track: ${track.kind}, camera: ${isCameraOn}, screenshare: ${isScreenSharing}`)

        // Pass LiveKit track reference for existing tracks too
        this.onTrackSubscribedCallbacks.forEach(callback =>
            callback(participantId, mediaStream, {
                isCameraOn,
                isScreenSharing,
                trackKind: track.kind,
                enabled: track.mediaStreamTrack.enabled,
                muted: publication.isMuted,
                hasTrack: true,
                isExisting: true,
                streamType: isScreenShare ? 'screen' : 'camera' // ADD THIS FLAG
            }, track)
        )
    }


    setupRoomEventHandlers() {
        if (!this.room) return

        // Connection state changes
        this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
            console.log('Connection state:', state)
            this.onConnectionStateCallbacks.forEach(callback => callback('all', state))
        })

        // Reconnecting event - fires when LiveKit detects connection loss and starts reconnecting
        this.room.on(RoomEvent.Reconnecting, () => {
            console.log('LiveKit: Connection lost, attempting to reconnect...')
            this.onConnectionStateCallbacks.forEach(callback =>
                callback('all', 'reconnecting')
            )
        })

        // Reconnected event - fires when LiveKit successfully reconnects
        this.room.on(RoomEvent.Reconnected, () => {
            console.log('LiveKit: Successfully reconnected to room')
            this.onConnectionStateCallbacks.forEach(callback =>
                callback('all', 'connected')
            )
        })

        // Participant joined - IMMEDIATELY add them to UI even without tracks
        // In ParticipantConnected event:
        this.room.on(RoomEvent.ParticipantConnected, (participant) => {
            console.log('Participant joined:', participant.identity)

            // FIXED: Only create streams if they don't already exist
            let cameraStream = this.participantCameraStreams.get(participant.identity)
            let screenStream = this.participantScreenStreams.get(participant.identity)

            if (!cameraStream) {
                cameraStream = this.createEmptyStream()
                this.participantCameraStreams.set(participant.identity, cameraStream)
            }

            if (!screenStream) {
                screenStream = this.createEmptyStream()
                this.participantScreenStreams.set(participant.identity, screenStream)
            }

            // Notify with camera stream (existing or new)
            this.onParticipantConnectedCallbacks.forEach(callback =>
                callback(participant.identity, cameraStream, {
                    isCameraOn: false,
                    isScreenSharing: false,
                    hasJoined: true,
                    isEmpty: true
                })
            )
        })

        // Participant left
        this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            console.log('Participant left:', participant.identity)

            // Clean up both streams
            const cameraStream = this.participantCameraStreams.get(participant.identity)
            const screenStream = this.participantScreenStreams.get(participant.identity)

            if (cameraStream) {
                cameraStream.getTracks().forEach(track => track.stop())
                this.participantCameraStreams.delete(participant.identity)
            }

            if (screenStream) {
                screenStream.getTracks().forEach(track => track.stop())
                this.participantScreenStreams.delete(participant.identity)
            }

            this.onParticipantDisconnectedCallbacks.forEach(callback =>
                callback(participant.identity)
            )
        })

        this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
            console.log(`Track subscribed: ${track.kind} from ${participant.identity}, sid: ${track.sid}, source: ${track.source}, enabled: ${track.mediaStreamTrack?.enabled}`)

            if (!track.mediaStreamTrack) {
                console.warn('Track has no mediaStreamTrack')
                return
            }

            const participantKey = participant.identity
            const isScreenShare = track.source === Track.Source.ScreenShare

            // Use separate stream maps for camera and screen share
            const streamMap = isScreenShare ? this.participantScreenStreams : this.participantCameraStreams

            // Get existing stream to preserve other tracks
            const existingStream = streamMap.get(participantKey)

            // CRITICAL: Always create a NEW MediaStream to trigger React updates
            const mediaStream = new MediaStream()

            // Copy tracks from existing stream, except same kind as new track
            if (existingStream) {
                existingStream.getTracks().forEach(existingTrack => {
                    if (existingTrack.kind !== track.kind) {
                        console.log(`Preserving existing ${existingTrack.kind} track: ${existingTrack.id}`)
                        mediaStream.addTrack(existingTrack)
                    } else {
                        console.log(`Removing old ${existingTrack.kind} track: ${existingTrack.id} (${existingTrack.readyState})`)
                    }
                })
            }

            // Add the new track
            console.log(`Adding ${track.kind} track to ${isScreenShare ? 'screen' : 'camera'} stream: ${track.mediaStreamTrack.id} to stream ${mediaStream.id}`)
            mediaStream.addTrack(track.mediaStreamTrack)

            // Store the NEW stream reference
            streamMap.set(participantKey, mediaStream)
            console.log(`Created new ${isScreenShare ? 'screen share' : 'camera'} stream for ${participantKey}: ${mediaStream.id}`)

            // Create a unique identifier for this specific track instance
            const trackInstanceId = `${participantKey}-${track.kind}-${track.sid}`
            this.processedTracks.add(trackInstanceId)

            // Handle track ending
            const handleTrackEnd = () => {
                console.log(`Track ended: ${track.kind} from ${participantKey}, sid: ${track.sid}`)
                this.processedTracks.delete(trackInstanceId)

                const currentStream = streamMap.get(participantKey)
                if (currentStream && currentStream.getTracks().includes(track.mediaStreamTrack)) {
                    console.log(`Removing ended track from stream: ${track.mediaStreamTrack.id}`)

                    // Create NEW stream without the ended track
                    const newStream = new MediaStream()
                    currentStream.getTracks().forEach(t => {
                        if (t !== track.mediaStreamTrack) {
                            newStream.addTrack(t)
                        }
                    })
                    streamMap.set(participantKey, newStream)

                    // Notify UI of the change
                    this.onTrackSubscribedCallbacks.forEach(callback =>
                        callback(participantKey, newStream, {
                            isCameraOn: track.kind === 'video' && !isScreenShare ? false : undefined,
                            isScreenSharing: isScreenShare ? false : undefined,
                            trackKind: track.kind,
                            enabled: false,
                            isUpdate: true,
                            streamType: isScreenShare ? 'screen' : 'camera'
                        }, null)
                    )
                }

                track.mediaStreamTrack.removeEventListener('ended', handleTrackEnd)
            }

            track.mediaStreamTrack.addEventListener('ended', handleTrackEnd)

            // Determine track properties
            const isScreenSharing = track.source === Track.Source.ScreenShare
            const isCameraOn = track.kind === 'video' && !isScreenSharing

            console.log(`Notifying about track: ${track.kind}, camera: ${isCameraOn}, screenshare: ${isScreenSharing}, enabled: ${track.mediaStreamTrack.enabled}`)

            // Pass the LiveKit track reference to callbacks
            this.onTrackSubscribedCallbacks.forEach(callback =>
                callback(participantKey, mediaStream, {
                    isCameraOn,
                    isScreenSharing,
                    trackKind: track.kind,
                    enabled: track.mediaStreamTrack.enabled,
                    muted: publication.isMuted,
                    hasTrack: true,
                    trackId: track.sid,
                    streamId: mediaStream.id,
                    streamType: isScreenShare ? 'screen' : 'camera'
                }, track)
            )
        })

        this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
            console.log(`Track unsubscribed: ${track.kind} from ${participant.identity}, sid: ${track.sid}, source: ${track.source}`)

            const participantKey = participant.identity
            const trackInstanceId = `${participantKey}-${track.kind}-${track.sid}`

            // Remove this specific track instance from processed tracks
            this.processedTracks.delete(trackInstanceId)

            // FIXED: Use correct stream map based on track source
            const isScreenShare = track.source === Track.Source.ScreenShare
            const streamMap = isScreenShare ? this.participantScreenStreams : this.participantCameraStreams
            const mediaStream = streamMap.get(participantKey)

            if (mediaStream) {
                // Only remove tracks that are actually ended or invalid
                const tracksToRemove = mediaStream.getTracks().filter(t => {
                    return t.kind === track.kind && (
                        t.id === track.mediaStreamTrack?.id &&
                        (t.readyState === 'ended' || !track.mediaStreamTrack)
                    )
                })

                if (tracksToRemove.length > 0) {
                    tracksToRemove.forEach(t => {
                        console.log(`Removing unsubscribed ${t.kind} track: ${t.id} (${t.readyState})`)
                        mediaStream.removeTrack(t)
                    })
                    console.log(`Stream ${mediaStream.id} now has ${mediaStream.getTracks().length} tracks`)
                } else {
                    console.log(`No tracks removed for ${track.kind} unsubscribe - track may be temporarily muted`)
                }
            }

            // Only notify UI about video track removal, not audio
            if (track.kind === 'video') {
                this.onTrackUnsubscribedCallbacks.forEach(callback =>
                    callback(participantKey, track.kind)
                )
            }
        })

        this.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
            console.log(`Track unmuted: ${publication.kind} from ${participant.identity}`)

            // Get the actual LiveKit track if available
            const livekitTrack = publication.track

            if (publication.kind === 'audio') {
                this.onTrackSubscribedCallbacks.forEach(callback =>
                    callback(participant.identity, null, {
                        trackKind: publication.kind,
                        enabled: true,
                        muted: false,
                        isUpdate: true,
                        preserveStream: true
                    }, livekitTrack) // <-- Pass LiveKit track reference
                )
            } else {
                this.onTrackSubscribedCallbacks.forEach(callback =>
                    callback(participant.identity, null, {
                        isCameraOn: publication.kind === 'video' ? true : undefined,
                        isScreenSharing: false,
                        trackKind: publication.kind,
                        enabled: true,
                        muted: false,
                        isUpdate: true
                    }, livekitTrack) // <-- Pass LiveKit track reference
                )
            }
        })

        this.room.on(RoomEvent.TrackMuted, (publication, participant) => {
            console.log(`Track muted: ${publication.kind} from ${participant.identity}`)

            // Get the actual LiveKit track if available
            const livekitTrack = publication.track

            if (publication.kind === 'audio') {
                this.onTrackSubscribedCallbacks.forEach(callback =>
                    callback(participant.identity, null, {
                        trackKind: publication.kind,
                        enabled: false,
                        muted: true,
                        isUpdate: true,
                        preserveStream: true
                    }, livekitTrack) // <-- Pass LiveKit track reference
                )
            } else {
                this.onTrackSubscribedCallbacks.forEach(callback =>
                    callback(participant.identity, null, {
                        isCameraOn: publication.kind === 'video' ? false : undefined,
                        isScreenSharing: false,
                        trackKind: publication.kind,
                        enabled: false,
                        muted: true,
                        isUpdate: true
                    }, livekitTrack) // <-- Pass LiveKit track reference
                )
            }
        })

        // Room disconnected
        this.room.on(RoomEvent.Disconnected, (reason) => {
            console.log('LiveKit: Permanently disconnected from room:', reason)
            this.isConnected = false
            this.onConnectionStateCallbacks.forEach(callback =>
                callback('all', 'disconnected')
            )
        })
    }

    onTrackUnsubscribed(callback) {
        if (!this.onTrackUnsubscribedCallbacks) {
            this.onTrackUnsubscribedCallbacks = []
        }
        this.onTrackUnsubscribedCallbacks.push(callback)
    }

    async enableCamera(enabled = true) {
        try {
            if (!this.room) throw new Error('Not connected to room')

            if (enabled && !this.isCameraEnabled) {
                console.log('Enabling camera (keeping screen share active if present)')

                const videoTrack = await createLocalVideoTrack({
                    resolution: VideoPresets.h720, // 1280x720
                    facingMode: 'user'
                })

                await this.room.localParticipant.publishTrack(videoTrack)
                this.localTracks.set('camera', videoTrack)
                this.isCameraEnabled = true

                console.log('Camera enabled')

            } else if (!enabled && this.isCameraEnabled) {
                console.log('Disabling camera')

                const videoTrack = this.localTracks.get('camera')
                if (videoTrack) {
                    await this.room.localParticipant.unpublishTrack(videoTrack)
                    await videoTrack.stop()
                    this.localTracks.delete('camera')
                }

                this.isCameraEnabled = false
                console.log('Camera disabled')
            }

            return this.isCameraEnabled
        } catch (error) {
            console.error('Failed to toggle camera:', error)
            throw error
        }
    }

    async enableMicrophone(enabled = true) {
        try {
            if (!this.room) throw new Error('Not connected to room')

            if (enabled && !this.isMicrophoneEnabled) {
                console.log('Enabling microphone')

                const audioTrack = await createLocalAudioTrack({
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                })

                await this.room.localParticipant.publishTrack(audioTrack)
                this.localTracks.set('microphone', audioTrack)
                this.isMicrophoneEnabled = true

                console.log('Microphone enabled')

            } else if (!enabled && this.isMicrophoneEnabled) {
                console.log('Disabling microphone')

                const audioTrack = this.localTracks.get('microphone')
                if (audioTrack) {
                    await this.room.localParticipant.unpublishTrack(audioTrack)
                    await audioTrack.stop()
                    this.localTracks.delete('microphone')
                }

                this.isMicrophoneEnabled = false
                console.log('Microphone disabled')
            }

            return this.isMicrophoneEnabled
        } catch (error) {
            console.error('Failed to toggle microphone:', error)
            throw error
        }
    }

    async enableScreenShare(enabled = true) {
        try {
            if (!this.room) throw new Error('Not connected to room')

            if (enabled && !this.isScreenSharing) {
                console.log('Starting screen share (keeping camera active)')

                try {
                    // Use Room's built-in screen sharing method
                    await this.room.localParticipant.setScreenShareEnabled(true, {
                        audio: false,
                        video: {
                            width: { ideal: 1920, max: 1920 },
                            height: { ideal: 1080, max: 1080 },
                            frameRate: { ideal: 30 },
                        },
                        // ADD: Encoding options for better quality
                        resolution: VideoPresets.h1080,
                        simulcast: false, // Disable simulcast for screen share - use full bandwidth
                    })

                    this.isScreenSharing = true

                    const screenTrackPublication = this.room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
                    if (screenTrackPublication && screenTrackPublication.track) {
                        const screenTrack = screenTrackPublication.track
                        this.localTracks.set('screenshare', screenTrack)

                        screenTrack.mediaStreamTrack.addEventListener('ended', async () => {
                            console.log('Screen share ended by user')
                            await this.enableScreenShare(false)
                        })
                    }

                    console.log('Screen share started, camera remains active')
                } catch (error) {
                    console.error('Screen share failed:', error)
                    throw error
                }

            } else if (!enabled && this.isScreenSharing) {
                console.log('Stopping screen share')

                // Use Room's built-in method to stop screen sharing
                await this.room.localParticipant.setScreenShareEnabled(false)

                // Clean up local track reference
                this.localTracks.delete('screenshare')
                this.isScreenSharing = false

                console.log('Screen share stopped, camera remains active')
            }

            return this.isScreenSharing
        } catch (error) {
            console.error('Failed to toggle screen share:', error)
            throw error
        }
    }

    // Get local media stream for VideoFeed component compatibility
    getLocalStream() {
        const tracks = []

        this.localTracks.forEach((track) => {
            if (track.mediaStreamTrack && track.mediaStreamTrack.readyState === 'live') {
                tracks.push(track.mediaStreamTrack)
            }
        })

        return tracks.length > 0 ? new MediaStream(tracks) : new MediaStream()
    }

    async leaveRoom() {
        try {
            console.log('Leaving room...')

            // Stop all local tracks
            for (const [name, track] of this.localTracks) {
                console.log(`Stopping ${name} track`)
                await track.stop()
            }
            this.localTracks.clear()

            // Disconnect from room
            if (this.room) {
                await this.room.disconnect()
                this.room = null
            }

            // Reset state
            this.isConnected = false
            this.isCameraEnabled = false
            this.isMicrophoneEnabled = false
            this.isScreenSharing = false

            console.log('Successfully left room')
        } catch (error) {
            console.error('Error leaving room:', error)
        }
    }

    // Callback registration methods (same interface as your WebRTC service)
    onParticipantConnected(callback) {
        this.onParticipantConnectedCallbacks.push(callback)
    }

    onParticipantDisconnected(callback) {
        this.onParticipantDisconnectedCallbacks.push(callback)
    }

    onTrackSubscribed(callback) {
        this.onTrackSubscribedCallbacks.push(callback)
    }

    onConnectionState(callback) {
        this.onConnectionStateCallbacks.push(callback)
    }

    // Alias methods for backward compatibility
    onRemoteStream(callback) {
        this.onTrackSubscribed(callback)
    }

    onParticipantStateChange(callback) {
        // LiveKit handles this automatically through track subscriptions
        this.onTrackSubscribed(callback)
    }

    getRoom() {
        return this.room;
    }

}

export default LiveKitService